/** PROTOTYPE flow step: configure each spread (module master/detail) -- THROWAWAY. */
import React, { useState } from 'react';
import { CRC } from './moduleInputsData';
import { Label } from './protoAtoms';
import {
  ProjectState, Spread, ModuleKey, MODULE_LABELS, money, SpreadOverrides,
  moduleCfg, moduleCfgOverridden, projectCfg, durationMonthsFor,
} from './spreadSplitData';
import { activeDiscountBand } from './spreadConfigDefs';
import { duplicateSpread, applyConfigToAll, deleteSpread } from './spreadSeed';
import { SpreadModuleDetail } from './SpreadModuleDetail';
import { ConfigDrawer } from './SpreadOverrideDrawer';
import { spreadPricing } from './spreadRollup';
import { SpreadPriceState } from './useSpreadPrice';
import { WeldMode } from './spreadPricingApi';

const MODULE_ORDER: ModuleKey[] = ['welding', 'mt', 'ndt', 'coating', 'platform', 'retention'];

interface Props {
  project: ProjectState; setProject: (p: ProjectState) => void;
  priceStates: Record<string, SpreadPriceState>;
  weldMode: WeldMode; setWeldMode: (m: WeldMode) => void;
  mobilizing: boolean; setMobilizing: (v: boolean) => void;
}

const tab = (active: boolean): React.CSSProperties => ({
  padding: '7px 13px', borderRadius: 999, fontSize: 12.5, fontWeight: active ? 600 : 500, cursor: 'pointer', fontFamily: 'inherit',
  background: active ? CRC.heroRed10 : '#fff', border: `1px solid ${active ? CRC.heroRed20 : CRC.border}`, color: active ? CRC.readableRed : CRC.blueprintBlue,
});

export const StepConfigure: React.FC<Props> = ({ project, setProject, priceStates, weldMode, setWeldMode, mobilizing, setMobilizing }) => {
  const [activeSpreadId, setActiveSpreadId] = useState<string>(project.spreads[0]?.id ?? '');
  const [moduleKey, setModuleKey] = useState<ModuleKey>('welding');
  const [drawer, setDrawer] = useState(false);
  const spread = project.spreads.find((s) => s.id === activeSpreadId) ?? project.spreads[0];

  const patchSpread = (id: string, patch: Partial<Spread>) =>
    setProject({ ...project, spreads: project.spreads.map((s) => (s.id === id ? { ...s, ...patch } : s)) });
  const patchOverrides = (s: Spread, patch: SpreadOverrides) => patchSpread(s.id, { overrides: { ...s.overrides, ...patch } });
  const patchModuleCfg = (s: Spread, module: ModuleKey, key: string, v: number | undefined) => {
    const modCfg: Record<string, number> = { ...(s.overrides.config?.[module]) };
    if (v === undefined) delete modCfg[key]; else modCfg[key] = v;
    patchOverrides(s, { config: { ...s.overrides.config, [module]: modCfg } });
  };
  const resetModuleCfg = (s: Spread, module: ModuleKey) => patchOverrides(s, { config: { ...s.overrides.config, [module]: {} } });
  const removeActive = () => {
    const remaining = project.spreads.filter((s) => s.id !== spread.id);
    setProject(deleteSpread(project, spread.id));
    if (remaining[0]) setActiveSpreadId(remaining[0].id);
  };

  if (!spread) return <div style={{ fontSize: 13, color: CRC.textGrey }}>No spreads yet — add some in the Spreads step.</div>;

  const spreadPrice: SpreadPriceState = priceStates[spread.id] ?? { status: 'loading' };
  const ready = spreadPrice.status === 'done';
  const pricing = spreadPricing(spreadPrice);

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {project.spreads.map((s) => (
          <button key={s.id} style={tab(s.id === spread.id)} onClick={() => setActiveSpreadId(s.id)}>{s.name}</button>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ ...tab(false), color: CRC.readableRed }} onClick={() => setProject(duplicateSpread(project, spread.id))}>Duplicate spread</button>
          <button style={tab(false)} onClick={() => setProject(applyConfigToAll(project, spread.id))}>Apply config to all spreads</button>
          {project.spreads.length > 1 && (
            <button style={{ ...tab(false), color: CRC.readableRed, borderColor: CRC.heroRed20 }} onClick={removeActive}>Delete spread</button>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <Label>Spread total</Label>
          <div style={{ fontFamily: 'Funnel Sans', fontSize: 20, fontWeight: 700, color: CRC.readableRed }}>{ready ? money(pricing.total) : '…'}</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '230px 1fr', gap: 18, alignItems: 'start' }}>
        <div style={{ background: '#fff', border: `1px solid ${CRC.border}`, borderRadius: 12, padding: 10, position: 'sticky', top: 16 }}>
          {MODULE_ORDER.map((k) => {
            const on = k === moduleKey;
            return (
              <button key={k} onClick={() => setModuleKey(k)}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', textAlign: 'left', padding: '10px', borderRadius: 8, marginBottom: 2, cursor: 'pointer', border: 'none', fontFamily: 'inherit', background: on ? CRC.heroRed10 : 'transparent' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: on ? CRC.blueprintBlue : CRC.textGrey }}>{MODULE_LABELS[k]}</span>
                <span style={{ fontFamily: 'Funnel Sans', fontSize: 11, fontWeight: 700, color: on ? CRC.readableRed : CRC.heritageGrey }}>{!spread.enabled[k] ? 'off' : !ready ? '…' : money(pricing[k].price)}</span>
              </button>
            );
          })}
        </div>

        <SpreadModuleDetail
          spread={spread} moduleKey={moduleKey} pricing={pricing}
          setMachine={(k, v) => patchSpread(spread.id, { machines: { ...spread.machines, [k]: v } })}
          setMtMills={(mills) => patchSpread(spread.id, { mtMills: mills })}
          setMtOptions={(o) => patchSpread(spread.id, { mtOptions: o })}
          setNdtSources={(s) => patchSpread(spread.id, { ndtSources: s })}
          setCoatingSources={(s) => patchSpread(spread.id, { coatingSources: s })}
          setPlatformOptions={(o) => patchSpread(spread.id, { platformOptions: o })}
          setRetentionOptions={(o) => patchSpread(spread.id, { retentionOptions: o })}
          setEnabled={(k, on) => patchSpread(spread.id, { enabled: { ...spread.enabled, [k]: on } })}
          onEditOverrides={() => setDrawer(true)}
          spreadState={spreadPrice} weldMode={weldMode} setWeldMode={setWeldMode}
          mobilizing={mobilizing} setMobilizing={setMobilizing} />
      </div>

      {drawer && (
        <ConfigDrawer
          title={`${spread.name} · ${MODULE_LABELS[moduleKey]} configuration`}
          subtitle="Inherits the project rate card. Edit any value to override it for this spread only."
          moduleKey={moduleKey}
          band={activeDiscountBand(durationMonthsFor(spread))}
          getValue={(k) => moduleCfg(project, spread, moduleKey, k)}
          getDefault={(k) => projectCfg(project, moduleKey, k)}
          isOverridden={(k) => moduleCfgOverridden(spread, moduleKey, k)}
          setCfg={(k, v) => patchModuleCfg(spread, moduleKey, k, v)}
          resetAll={() => resetModuleCfg(spread, moduleKey)}
          resetLabel={`Reset ${MODULE_LABELS[moduleKey]} to project defaults`}
          onClose={() => setDrawer(false)} />
      )}
    </div>
  );
};
